import { AnimatePresence, motion } from 'framer-motion';
import { useEffect, useRef, useState } from 'react';
import { useGameStore } from '../store/gameStore';

/**
 * DeathModal — compact + quick-respawn focused.
 *
 * Previous version put the "Play Again" button at the bottom of a tall
 * panel with mass + kills + share + home all competing for attention.
 * New version inverts the hierarchy: a BIG square "respawn" button is
 * the dominant element, sized so it's literally hard to miss. Stats
 * are a small caption below. Secondary actions (share, home) drop to
 * a thin row at the bottom.
 *
 * Choreography unchanged from the prior restrained build:
 *   t = 0.00s  backdrop fade + light blur
 *   t = 0.08s  panel + content settle in together
 */
export function DeathModal({
  alive,
  mass,
  playerHue,
  respawn,
  onHome,
  kills = 0,
  bestMass = 0,
  killedByName,
  killedByHue,
  rank = 0,
  totalPlayers = 0,
  topThree = [],
}: {
  alive: boolean;
  mass: number;
  playerHue: number;
  respawn: (() => void) | null;
  onHome?: () => void;
  /** Snake kills the player scored this life. */
  kills?: number;
  /** All-time best mass — highlighted if this run beat it. */
  bestMass?: number;
  /** Identity of the snake that killed you (if any). */
  killedByName?: string | null;
  killedByHue?: number | null;
  /** Final leaderboard rank at the moment of death (1-based).
   *  0 / undefined means we didn't compute one (e.g. died before the
   *  first leaderboard tick). */
  rank?: number;
  /** Snake count on the leaderboard snapshot used for `rank` —
   *  drives the "#7 of 30" denominator. */
  totalPlayers?: number;
  /** Top 3 snakes on the leaderboard at the moment of death.
   *  Shown as a small podium under the rank pill — gives the
   *  player a "one more run and I could overtake them" mental
   *  anchor that drives the respawn decision. */
  topThree?: Array<{ name: string; mass: number; hue: number; isPlayer?: boolean }>;
}) {
  const color = `hsl(${playerHue} 100% 65%)`;
  const beatBest = Math.round(mass) >= bestMass && bestMass > 0;
  const [shareLabel, setShareLabel] = useState('Share');

  // ── Session-lives streak ──────────────────────────────────────
  //
  // Track how many lives the player has played in this browser
  // session. Increments whenever `alive` flips true → false (death).
  // Shown to the player on the death modal as gentle motivation:
  // "🔥 5th try this session" frames the next attempt as a streak
  // rather than a fresh failure. Resets only when the component
  // unmounts (player leaves the game entirely).
  const livesRef = useRef(0);
  const prevAliveRef = useRef(alive);
  useEffect(() => {
    if (prevAliveRef.current && !alive) {
      livesRef.current += 1;
    }
    prevAliveRef.current = alive;
  }, [alive]);
  const livesThisSession = livesRef.current;

  // ── Auto-respawn countdown ────────────────────────────────────
  //
  // Per the retention analysis, only 20.5 % of players respawn after
  // dying — meaning 4 out of 5 sessions ENDED at the first death even
  // though the player had a working "play again" button right in
  // front of them. The fix isn't to make the button bigger; it's to
  // make doing NOTHING the same as "play again". A 5-second visible
  // countdown automatically respawns the player unless they explicitly
  // bail out, flipping the default action from "close tab" to "keep
  // playing".
  //
  // Cancelled by: clicking Home, clicking Share, or hovering / clicking
  // anywhere outside the respawn button (any action signals "I want to
  // make a choice"). Clicking the respawn button itself respawns
  // immediately (short-circuits the countdown).
  // 3 seconds rather than 5. The retention audit found the auto-
  // respawn rate at 16.7% (down from 20.5% — a worsening trend that
  // suggested the 5-second wait was giving the player too much time
  // to drift to the close-tab decision. 3s keeps the body-scatter
  // beat (~800ms) + recap pause (~2s) for emotion, then snaps them
  // back into the game.
  const AUTO_RESPAWN_SECONDS = 3;
  const [secondsLeft, setSecondsLeft] = useState(AUTO_RESPAWN_SECONDS);
  const [autoRespawnActive, setAutoRespawnActive] = useState(false);
  const intervalRef = useRef<number | null>(null);
  const clearCountdown = (): void => {
    if (intervalRef.current !== null) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
    setAutoRespawnActive(false);
  };

  useEffect(() => {
    if (alive || !respawn) {
      clearCountdown();
      setSecondsLeft(AUTO_RESPAWN_SECONDS);
      return;
    }
    // Start the countdown 800 ms after death so the player sees the
    // recap stats first — slamming respawn immediately would feel
    // disrespectful of the run that just ended.
    setSecondsLeft(AUTO_RESPAWN_SECONDS);
    const startTimer = window.setTimeout(() => {
      setAutoRespawnActive(true);
      intervalRef.current = window.setInterval(() => {
        setSecondsLeft((s) => {
          if (s <= 1) {
            clearCountdown();
            respawn();
            return 0;
          }
          return s - 1;
        });
      }, 1000);
    }, 800);
    return () => {
      window.clearTimeout(startTimer);
      clearCountdown();
    };
  }, [alive, respawn]);

  // ── Comeback delta — distance to overtake the next rank up. ────
  //
  // If the player isn't already #1, surface the gap between their mass
  // and the next-better rival's mass. "Only 240 mass behind ECHO" is
  // a far stronger respawn motivator than just "you finished #4" —
  // it converts a passive result into an active goal.
  const comebackTarget = (() => {
    if (!topThree.length || rank <= 1) return null;
    // The target is the slot DIRECTLY above us. If we finished #2 →
    // target is #1. If we finished out of top 3 → target is #3.
    const targetIdx = rank <= topThree.length ? rank - 2 : topThree.length - 1;
    const target = topThree[targetIdx];
    if (!target || target.isPlayer) return null;
    const gap = Math.max(0, Math.round(target.mass - mass));
    if (gap <= 0) return null;
    return { gap, name: target.name, hue: target.hue };
  })();

  // ── Result-aware headline ──────────────────────────────────────
  //
  // The single most-read string in the modal. Tunes the emotional
  // frame from "you finished" (neutral end) to "SO CLOSE" (charged,
  // unfinished business) when the player landed near the top. Drives
  // the respawn decision more than any stat below.
  const headline = (() => {
    if (rank > 0 && totalPlayers > 0) {
      if (rank === 1) return { text: '👑 Victory', accent: '#ffd23d' };
      if (rank <= 3) return { text: 'Podium finish', accent: color };
      if (rank <= 10 && totalPlayers >= 10) return { text: 'So close!', accent: color };
    }
    return { text: 'Run over', accent: null as string | null };
  })();

  // Global keyboard hotkeys: Space / Enter respawn directly without
  // requiring the player to mouse over the modal. The whole goal of
  // this round is to *push* the player back into the arena instead of
  // letting them drift into the home screen — a thumb on Space should
  // be the fastest path back. Listener only attaches while the modal
  // is visible to avoid swallowing keystrokes during normal play.
  useEffect(() => {
    if (alive || !respawn) return;
    const onKey = (e: KeyboardEvent): void => {
      const t = e.target as HTMLElement | null;
      const inField = t && (t.tagName === 'INPUT' || t.tagName === 'TEXTAREA');
      if (inField) return;
      if (e.code === 'Space' || e.code === 'Enter') {
        e.preventDefault();
        respawn();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [alive, respawn]);
  // Theme toggle exposed inline so the player can flip light/dark
  // between matches without opening the Settings modal.
  const theme = useGameStore((s) => s.theme);
  const toggleTheme = useGameStore((s) => s.toggleTheme);

  const share = async (): Promise<void> => {
    clearCountdown();
    const text = `🐍 CellSnake run: ${Math.round(mass)} mass · ${kills} kill${kills === 1 ? '' : 's'}${beatBest ? ' (new best)' : ''}. Play at https://cellsnake.com`;
    try {
      if (navigator.share) {
        await navigator.share({ text, title: 'CellSnake' });
        setShareLabel('Shared ✓');
      } else {
        await navigator.clipboard.writeText(text);
        setShareLabel('Copied ✓');
      }
    } catch {
      setShareLabel('Failed');
    }
    setTimeout(() => setShareLabel('Share'), 2000);
  };

  return (
    <AnimatePresence>
      {!alive && (
        <motion.div
          className="absolute inset-0 flex items-center justify-center pointer-events-auto"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1, transition: { duration: 0.22 } }}
          exit={{ opacity: 0, transition: { duration: 0.18 } }}
          style={{ background: 'radial-gradient(ellipse at center, rgba(0,0,0,0.55) 0%, rgba(0,0,0,0.88) 70%)' }}
        >
          <motion.div
            className="absolute inset-0 pointer-events-none"
            initial={{ backdropFilter: 'blur(0px)' }}
            animate={{ backdropFilter: 'blur(2.5px)' }}
            transition={{ duration: 0.25, ease: 'easeOut' }}
          />

          <motion.div
            initial={{ scale: 0.94, opacity: 0, y: 4 }}
            animate={{
              scale: 1, opacity: 1, y: 0,
              transition: { type: 'spring', stiffness: 460, damping: 32, delay: 0.04 },
            }}
            exit={{ scale: 0.96, opacity: 0, transition: { duration: 0.14 } }}
            className="relative w-[340px] overflow-hidden rounded-2xl"
            style={{
              background:
                'linear-gradient(180deg, rgba(18,21,32,0.96) 0%, rgba(10,12,20,0.98) 100%)',
              border: '1px solid rgba(255,255,255,0.08)',
              boxShadow: `0 24px 80px -20px ${color}33, 0 0 0 1px rgba(255,255,255,0.04) inset, 0 1px 0 0 rgba(255,255,255,0.06) inset`,
            }}
          >
            {/* Top color accent — thin gradient bar in the player's hue,
                ties the modal to their snake identity without making
                the whole panel scream color. */}
            <div
              className="absolute top-0 left-0 right-0 h-[2px]"
              style={{
                background: `linear-gradient(90deg, transparent 0%, ${color} 50%, transparent 100%)`,
                opacity: 0.7,
              }}
            />

            <div className="px-7 pt-6 pb-5 text-center">
              {/* Result-aware headline. "SO CLOSE" frames a near-top
                  finish as unfinished business, charging the respawn
                  decision. */}
              <div
                className="text-[10px] uppercase tracking-[0.4em] mb-3 font-bold"
                style={{
                  color: headline.accent || 'rgba(255,255,255,0.5)',
                  textShadow: headline.accent ? `0 0 12px ${headline.accent}66` : 'none',
                }}
              >
                {headline.text}
              </div>

              {/* ── Hero stat: the final mass, big and unmissable ──
                  Tabular nums so the digit widths stay locked while the
                  number animates in. If this beat the personal best, the
                  number glows in the player's color and a "new best"
                  micro-badge sits underneath. */}
              <div
                className="font-bold leading-none tabular-nums mb-1"
                style={{
                  fontSize: '56px',
                  color: beatBest ? color : '#ffffff',
                  textShadow: beatBest
                    ? `0 0 24px ${color}66, 0 0 48px ${color}33`
                    : '0 2px 12px rgba(0,0,0,0.4)',
                  letterSpacing: '-0.02em',
                }}
              >
                {Math.round(mass).toLocaleString()}
              </div>
              <div className="text-[10px] uppercase tracking-[0.32em] text-white/45 mb-1">
                final mass
              </div>

              {beatBest && (
                <motion.div
                  initial={{ opacity: 0, y: -4 }}
                  animate={{ opacity: 1, y: 0, transition: { delay: 0.2 } }}
                  className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[9px] uppercase tracking-[0.28em] font-semibold mb-3"
                  style={{
                    background: `linear-gradient(90deg, ${color}22, ${color}11)`,
                    border: `1px solid ${color}55`,
                    color,
                    textShadow: `0 0 8px ${color}aa`,
                  }}
                >
                  <span>★</span> new best
                </motion.div>
              )}

              {/* Rank line — "you finished #7 of 30". The single most
                  meaningful "I accomplished something" stat for a short
                  run. Even a 30-second player who reached top-half feels
                  like they're getting better, which pushes the respawn
                  decision toward yes. Top-3 finishes get a special gold
                  pill; 4-10 a silver one; everything else just shows
                  the rank in plain text so the celebratory chrome
                  stays meaningful. */}
              {rank > 0 && totalPlayers > 0 && (() => {
                const top3 = rank <= 3 && totalPlayers >= 5;
                const top10 = !top3 && rank <= 10 && totalPlayers >= 10;
                if (top3) {
                  return (
                    <motion.div
                      initial={{ opacity: 0, y: -4 }}
                      animate={{ opacity: 1, y: 0, transition: { delay: 0.3 } }}
                      className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-[9px] uppercase tracking-[0.28em] font-bold mb-3 ml-2"
                      style={{
                        background: 'linear-gradient(90deg, rgba(251,191,36,0.25), rgba(251,191,36,0.1))',
                        border: '1px solid rgba(251,191,36,0.55)',
                        color: 'rgb(252 211 77)',
                        textShadow: '0 0 8px rgba(251,191,36,0.7)',
                      }}
                    >
                      <span>🏆</span> #{rank} of {totalPlayers}
                    </motion.div>
                  );
                }
                if (top10) {
                  return (
                    <motion.div
                      initial={{ opacity: 0, y: -4 }}
                      animate={{ opacity: 1, y: 0, transition: { delay: 0.3 } }}
                      className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-[9px] uppercase tracking-[0.28em] font-bold mb-3 ml-2"
                      style={{
                        background: 'linear-gradient(90deg, rgba(229,231,235,0.15), rgba(229,231,235,0.05))',
                        border: '1px solid rgba(229,231,235,0.35)',
                        color: 'rgb(229 231 235)',
                      }}
                    >
                      #{rank} of {totalPlayers}
                    </motion.div>
                  );
                }
                return (
                  <div className="text-[10px] uppercase tracking-[0.3em] text-white/55 mb-3">
                    #{rank} of {totalPlayers}
                  </div>
                );
              })()}

              {/* Mini-leaderboard — the top 3 at the moment of death.
                  Shown beneath the rank pill so the player sees "I was
                  X mass away from the top 3" — that gap is the hook
                  that drives the respawn decision. Suppressed when the
                  leaderboard isn't populated yet. */}
              {topThree.length >= 3 && (
                <motion.div
                  initial={{ opacity: 0, y: -4 }}
                  animate={{ opacity: 1, y: 0, transition: { delay: 0.35 } }}
                  className="mb-4 mt-1"
                >
                  <div className="text-[8px] uppercase tracking-[0.4em] text-white/35 mb-2 text-center">
                    top 3
                  </div>
                  <div className="space-y-1">
                    {topThree.slice(0, 3).map((row, i) => {
                      const medal = i === 0 ? '🥇' : i === 1 ? '🥈' : '🥉';
                      return (
                        <div
                          key={`${row.name}-${i}`}
                          className="flex items-center justify-between gap-3 px-3 py-1.5 rounded-lg"
                          style={{
                            background: row.isPlayer
                              ? `linear-gradient(90deg, ${color}26, ${color}10)`
                              : 'rgba(255,255,255,0.025)',
                            border: row.isPlayer
                              ? `1px solid ${color}55`
                              : '1px solid rgba(255,255,255,0.04)',
                          }}
                        >
                          <div className="flex items-center gap-2 min-w-0">
                            <span className="text-xs" aria-hidden>{medal}</span>
                            <span
                              className="text-[12px] font-semibold tracking-wide truncate"
                              style={{
                                color: row.isPlayer ? 'white' : `hsl(${row.hue} 95% 72%)`,
                              }}
                            >
                              {row.name || '—'}
                            </span>
                          </div>
                          <span className="text-[11px] text-white/65 tabular-nums font-mono">
                            {Math.round(row.mass).toLocaleString()}
                          </span>
                        </div>
                      );
                    })}
                  </div>
                </motion.div>
              )}

              {/* Secondary stat row — kills + killer attribution. Thin
                  divider above keeps it visually grouped with the hero
                  number but signals "supporting info, not headline". */}
              <div
                className="flex items-center justify-center gap-4 text-[11px] tabular-nums mt-3 mb-5 pt-3"
                style={{ borderTop: '1px solid rgba(255,255,255,0.06)' }}
              >
                <span className="text-white/55">
                  <span className="text-white font-semibold">{kills}</span>
                  <span className="ml-1.5 text-[9px] uppercase tracking-[0.22em] text-white/40">
                    {kills === 1 ? 'kill' : 'kills'}
                  </span>
                </span>
                {killedByName && killedByHue != null && (
                  <>
                    <span className="text-white/15">•</span>
                    <span className="text-white/55">
                      <span className="text-[9px] uppercase tracking-[0.22em] text-white/40 mr-1.5">
                        taken down by
                      </span>
                      <span
                        className="font-semibold"
                        style={{
                          color: `hsl(${killedByHue} 95% 72%)`,
                          textShadow: `0 0 8px hsl(${killedByHue} 95% 60% / 0.4)`,
                        }}
                      >
                        {killedByName}
                      </span>
                    </span>
                  </>
                )}
              </div>

              {/* ── Comeback delta — "Only X mass behind RIVAL" ──
                  Surfaces a concrete gap to the next-better rank so the
                  player has a tangible respawn goal. Hidden when the
                  player won (rank 1) or finished outside the top-3
                  pool entirely (no clear target). */}
              {comebackTarget && (
                <motion.div
                  initial={{ opacity: 0, y: -4 }}
                  animate={{ opacity: 1, y: 0, transition: { delay: 0.4 } }}
                  className="text-center mb-3"
                >
                  <div className="text-[10px] uppercase tracking-[0.28em] text-white/55">
                    only
                    <span
                      className="mx-1 text-white font-bold tabular-nums"
                      style={{ textShadow: '0 0 8px rgba(255,255,255,0.4)' }}
                    >
                      {comebackTarget.gap.toLocaleString()}
                    </span>
                    mass behind{' '}
                    <span
                      className="font-bold"
                      style={{
                        color: `hsl(${comebackTarget.hue} 95% 72%)`,
                        textShadow: `0 0 8px hsl(${comebackTarget.hue} 95% 60% / 0.6)`,
                      }}
                    >
                      {comebackTarget.name}
                    </span>
                  </div>
                </motion.div>
              )}

              {/* ── Hero CTA: circular respawn button with concentric
                  pulse rings ──
                  Layered visuals:
                    - 2 outward-expanding ghost rings (animated opacity
                      + scale) — gives the "incoming, come back" feel
                      without the previous breathing-button hack
                    - Solid disc with radial gradient in player color
                    - White ↻ glyph centered, glows in player color
                  Hover/tap springs are subtle (1.05 / 0.96) — the button
                  already commands attention; we don't need to oversell.
                  Auto-focus + global Space/Enter listener mean any
                  thumb-twitch respawns. */}
              <div className="relative flex items-center justify-center mb-4">
                <motion.span
                  className="absolute rounded-full pointer-events-none"
                  style={{
                    width: 112, height: 112,
                    border: `2px solid ${color}`,
                  }}
                  animate={{ scale: [1, 1.45], opacity: [0.5, 0] }}
                  transition={{ duration: 1.6, repeat: Infinity, ease: 'easeOut' }}
                />
                <motion.span
                  className="absolute rounded-full pointer-events-none"
                  style={{
                    width: 112, height: 112,
                    border: `2px solid ${color}`,
                  }}
                  animate={{ scale: [1, 1.45], opacity: [0.5, 0] }}
                  transition={{ duration: 1.6, repeat: Infinity, ease: 'easeOut', delay: 0.8 }}
                />
                <motion.button
                  onClick={() => respawn?.()}
                  disabled={!respawn}
                  autoFocus
                  whileHover={{ scale: 1.06 }}
                  whileTap={{ scale: 0.94 }}
                  className="relative w-28 h-28 rounded-full flex items-center justify-center transition-shadow focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/50 disabled:opacity-40 disabled:cursor-not-allowed"
                  style={{
                    background: `radial-gradient(circle at 35% 30%, ${color}ff 0%, ${color}aa 45%, ${color}55 100%)`,
                    boxShadow: `0 0 48px ${color}88, 0 8px 32px ${color}44, inset 0 -8px 24px rgba(0,0,0,0.3), inset 0 4px 12px rgba(255,255,255,0.15)`,
                  }}
                  aria-label="Play again"
                >
                  <span
                    className="text-white font-bold leading-none"
                    style={{
                      fontSize: 44,
                      textShadow: `0 2px 8px rgba(0,0,0,0.4)`,
                    }}
                  >
                    ↻
                  </span>
                </motion.button>
              </div>

              <div className="text-[11px] uppercase tracking-[0.36em] text-white font-semibold mb-1.5">
                Play again
              </div>
              {/* Auto-respawn countdown — visible only while the timer
                  is running. The "cancel" link cancels it for players
                  who want to take their time / use a secondary action
                  (Share, theme toggle, Home). */}
              {autoRespawnActive ? (
                <div className="text-[10px] uppercase tracking-[0.24em] flex items-center justify-center gap-2 mt-0.5">
                  <span style={{ color }}>
                    Respawning in{' '}
                    <span className="font-bold tabular-nums text-white" style={{ textShadow: `0 0 8px ${color}aa` }}>
                      {secondsLeft}
                    </span>
                    …
                  </span>
                  <button
                    onClick={clearCountdown}
                    className="text-white/45 hover:text-white/85 transition-colors underline-offset-2 hover:underline"
                  >
                    cancel
                  </button>
                </div>
              ) : (
                <div className="text-[9px] uppercase tracking-[0.24em] text-white/45">
                  press 
                  <kbd className="px-1.5 py-0.5 rounded-md border border-white/15 bg-white/[0.04] text-white/85 font-mono not-italic text-[8px] mx-0.5">
                    Space
                  </kbd>
                  or{' '}
                  <kbd className="px-1.5 py-0.5 rounded-md border border-white/15 bg-white/[0.04] text-white/85 font-mono not-italic text-[8px] mx-0.5">
                    Enter
                  </kbd>
                </div>
              )}

              {/* Session lives streak — gentle "you're on a run" cue.
                  Shown from the 2nd life onward so a first-time death
                  isn't framed as "you have a streak" (you don't yet). */}
              {livesThisSession >= 2 && (
                <div className="mt-3 text-center text-[10px] tracking-[0.2em] uppercase">
                  <span className="text-amber-200/70">🔥 run #{livesThisSession + 1} this session</span>
                </div>
              )}

              {/* Secondary actions — small, low-emphasis row at the
                  bottom. Theme toggle stays here because the player
                  often realizes they want a contrast change between
                  matches and shouldn't have to dig into Settings. */}
              <div
                className="flex items-center justify-center gap-4 text-[9px] uppercase tracking-[0.24em] mt-5 pt-4"
                style={{ borderTop: '1px solid rgba(255,255,255,0.05)' }}
              >
                <button
                  onClick={share}
                  className="text-white/40 hover:text-white/85 transition-colors"
                  aria-label="Share your result"
                >
                  {shareLabel}
                </button>
                <span className="text-white/10">•</span>
                <button
                  onClick={toggleTheme}
                  className="text-white/40 hover:text-white/85 transition-colors"
                  aria-label="Toggle theme"
                >
                  {theme === 'dark' ? '☾' : '☀'}
                </button>
                {onHome && (
                  <>
                    <span className="text-white/10">•</span>
                    <button
                      onClick={() => { clearCountdown(); onHome(); }}
                      className="text-white/40 hover:text-white/85 transition-colors"
                      title="Back home — change color / pattern"
                    >
                      home
                    </button>
                  </>
                )}
              </div>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
